// Type-ahead location picker. Debounces user input, queries
// GET /api/tags?category=location&q=<query>&limit=20, shows results
// grouped by their ancestor path so "Rosebank" disambiguates between
// JHB (Rosebank JHB) and Cape Town (Rosebank).
//
// Chosen tag is passed up via onChange. Empty state prompts the user
// to type. No-match state notes that we might need to add their area
// (deferred UX — for now just say "not found").

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../lib/api';

interface Tag {
    id:            string;
    slug:          string;
    display_name:  string;
    depth:         number;
    parent_tag_id?: string | null;
}

interface Props {
    /** Currently selected tag id, or empty string. */
    value:    string;
    onChange: (tagID: string, tagLabel: string) => void;
    placeholder?: string;
    /** When true, show a hint about what to do if the location isn't listed. */
    allowMissingHint?: boolean;
}

export function LocationAutocomplete({ value, onChange, placeholder = "Type your suburb (e.g. Vredehoek, Newlands, Sea Point)", allowMissingHint = true }: Props) {
    const api = useApi();
    const [query, setQuery]       = useState('');
    const [debounced, setDebounced] = useState('');
    const [results, setResults]   = useState<Tag[]>([]);
    const [open, setOpen]         = useState(false);
    const [chosen, setChosen]     = useState<Tag | null>(null);
    const boxRef = useRef<HTMLDivElement | null>(null);

    // Debounce user typing — 250ms after last keystroke fires the search.
    useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), 250);
        return () => clearTimeout(t);
    }, [query]);

    // Fetch results whenever the debounced query changes (2+ chars).
    useEffect(() => {
        if (debounced.length < 2) { setResults([]); return; }
        const params = new URLSearchParams({
            category: 'location', q: debounced, limit: '20',
        });
        api.get<{ items: Tag[] }>('/api/tags?' + params.toString())
            .then(r => setResults(r.items ?? []))
            .catch(() => setResults([]));
    }, [api, debounced]);

    // If parent form clears value, clear the chosen tag too.
    useEffect(() => {
        if (!value) setChosen(null);
    }, [value]);

    // Close on outside click.
    useEffect(() => {
        function onDoc(e: MouseEvent) {
            if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
        }
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const pick = useCallback((t: Tag) => {
        setChosen(t);
        setQuery('');
        setOpen(false);
        onChange(t.id, t.display_name);
    }, [onChange]);

    const clear = useCallback(() => {
        setChosen(null);
        setQuery('');
        onChange('', '');
    }, [onChange]);

    return (
        <div ref={boxRef} style={{ position: 'relative' }}>
            {chosen ? (
                <div style={chosenChip}>
                    <span style={{ fontFamily: "'Newsreader', serif", fontWeight: 600 }}>
                        {chosen.display_name}
                    </span>
                    <button type="button" onClick={clear} style={clearBtn} title="Change location">×</button>
                </div>
            ) : (
                <input
                    type="text"
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={placeholder}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--ink)', fontSize: 14 }}
                />
            )}

            {open && !chosen && debounced.length >= 2 && (
                <div style={dropdown}>
                    {results.length === 0 ? (
                        <div style={{ padding: 10, color: '#888', fontSize: 12 }}>
                            No match for "{debounced}"
                            {allowMissingHint && (
                                <div style={{ marginTop: 6, fontFamily: "'Newsreader', serif", fontStyle: 'italic' }}>
                                    Don't see your area? Pick the nearest one for now — we'll add finer suburbs as communities register.
                                </div>
                            )}
                        </div>
                    ) : (
                        results.map(t => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => pick(t)}
                                style={resultRow}
                            >
                                <span style={{ fontFamily: "'Newsreader', serif", fontSize: 14 }}>
                                    {t.display_name}
                                </span>
                                <span style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 1 }}>
                                    depth {t.depth}
                                </span>
                            </button>
                        ))
                    )}
                </div>
            )}
            {open && !chosen && debounced.length < 2 && query.length > 0 && (
                <div style={{ ...dropdown, padding: 10, color: '#888', fontSize: 12 }}>
                    Keep typing…
                </div>
            )}
        </div>
    );
}

const chosenChip: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '8px 12px', border: '1px solid var(--ink)', background: '#faf8f2',
};
const clearBtn: React.CSSProperties = {
    marginLeft: 'auto', background: 'none', border: 'none', color: '#888',
    fontSize: 18, cursor: 'pointer', padding: '0 4px',
};
const dropdown: React.CSSProperties = {
    position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 2,
    background: '#fff', border: '1px solid var(--ink)',
    maxHeight: 260, overflow: 'auto', zIndex: 100,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
};
const resultRow: React.CSSProperties = {
    display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
    width: '100%', padding: '8px 12px', textAlign: 'left',
    background: 'none', border: 'none', borderBottom: '1px dotted #eee',
    cursor: 'pointer', font: 'inherit',
};
